import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { useNavigate } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconShipmentNew from '@/icons/ShipmentNew.svg';
import { useCompleteProcessing } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';

import { WorkshopPaymentStrategyInput } from '../RepairArticleAction';
import { TrackingNumberInput } from '../shared/TrackingNumberInput';

export const CompleteProcessingButton = ({
  onActionDone,
  mode,
  isDisabled,
  handleCreateShipment,
  isLoadingCreateShipment,
}: {
  onActionDone: () => void;
  mode: 'confirm-reception' | 'create-shipment';
  isDisabled: boolean;
  handleCreateShipment?: () => Promise<void>;
  isLoadingCreateShipment?: boolean;
}) => {
  const { article } = useArticleContext();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const label =
    mode === 'confirm-reception' ? (
      <Trans id="article.actions.processing.confirm">Confirm reception</Trans>
    ) : (
      <Trans id="article.actions.create-shipment">Create a shipment</Trans>
    );

  const serviceAdjustmentEnabled =
    article.step?.step === 'processing' && !!article.step?.config?.serviceAdjustmentEnabled;

  const shouldShowPaymentStrategyInput =
    !article.workshopPaymentStrategy &&
    article.workshopOrganization?.workshopConfig?.payoutStrategy === 'handled-by-organization' &&
    !serviceAdjustmentEnabled;

  const handleClick = async () => {
    if (mode === 'confirm-reception' || shouldShowPaymentStrategyInput) {
      setIsDialogOpen(true);
    } else {
      await handleCreateShipment?.();
    }
  };

  return (
    <>
      <Button
        variant={mode === 'confirm-reception' ? 'primary' : 'secondary'}
        size="medium"
        className="text-no-wrap"
        onClick={handleClick}
        disabled={isDisabled}
        isLoading={isLoadingCreateShipment}
      >
        {mode === 'create-shipment' && <IconShipmentNew />}
        {label}
      </Button>
      <Dialog title={label} isOpen={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <CompleteProcessingDialogContent
          onActionDone={() => {
            setIsDialogOpen(false);
            onActionDone();
          }}
          shouldShowPaymentStrategyInput={shouldShowPaymentStrategyInput}
          mode={mode}
        />
      </Dialog>
    </>
  );
};

const CompleteProcessingDialogContent = ({
  onActionDone,
  shouldShowPaymentStrategyInput,
  mode,
}: {
  onActionDone: () => void;
  shouldShowPaymentStrategyInput: boolean;
  mode: 'confirm-reception' | 'create-shipment';
}) => {
  const { article } = useArticleContext();
  const { t } = useLingui();
  const navigate = useNavigate();

  const [trackingNumber, setTrackingNumber] = useState<string | undefined>(undefined);
  const [paymentStrategy, setPaymentStrategy] = useState(article.workshopPaymentStrategy);

  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | undefined>();

  const { mutateAsync: confirmProcessing, isPending: isConfirmProcessingPending } =
    useCompleteProcessing({ articleId: article.id });

  const hasError = shouldShowPaymentStrategyInput && !paymentStrategy;

  const submit = async () => {
    setShowErrors(true);

    if (hasError) {
      return;
    }

    try {
      await confirmProcessing({
        trackingNumber,
        paymentStrategy: paymentStrategy ?? undefined,
        mode,
      });

      if (mode === 'confirm-reception') {
        onActionDone();
      } else {
        navigate('/shipments/new', {
          state: { requestId: article.requestId, articleId: article.id },
        });
      }
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <main>
        <Stack gap="1rem">
          {mode === 'confirm-reception' && (
            <Paragraph>
              <Trans id="article.actions.processing.confirm.text">
                By confirming, you acknowledge the reception of the item.
              </Trans>
            </Paragraph>
          )}
          {shouldShowPaymentStrategyInput && (
            <WorkshopPaymentStrategyInput
              paymentStrategy={paymentStrategy}
              setPaymentStrategy={setPaymentStrategy}
            />
          )}
          {mode === 'confirm-reception' && (
            <TrackingNumberInput
              trackingNumber={trackingNumber}
              setTrackingNumber={setTrackingNumber}
            />
          )}
        </Stack>
      </main>
      <Footer>
        {(!!apiError || (showErrors && hasError)) && (
          <FooterError>
            {apiError}
            {showErrors && hasError && (
              <Trans id="article.actions.processing.confirm.error">
                Please fill the missing information
              </Trans>
            )}
          </FooterError>
        )}
        <FooterButtons>
          <Button
            variant="primary"
            size="medium"
            onPress={submit}
            isLoading={isConfirmProcessingPending}
          >
            {mode === 'confirm-reception' ? (
              <Trans id="article.actions.processing.confirm.button">Confirm</Trans>
            ) : (
              <Trans id="article.actions.create-shipment">Create a shipment</Trans>
            )}
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
